import React, { useContext, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { sessionContext } from '@marketing-milk/frontend'
import {
  AdCreative,
  AssetApprovalStatus,
  CreativeReviewTrail,
  Permissions,
} from '@marketing-milk/interfaces'
import {
  useCreateStrategyGroup,
  useDeleteStrategyGroup,
  useMoveStrategyAd,
  usePacketStrategy,
  useRegenerateStrategy,
  useSprintPacket,
  useUnpinStrategyAd,
  useUpdateStrategyGroup,
} from 'hooks/api/useSprintPackets'
import { useSprint } from 'hooks/api/useSprint'
import { useAdCreativeReview } from 'hooks/api/useAdCreativeReview'
import { useUpdateCreativeCopy } from 'hooks/api/useUpdateCreativeCopy'
import { usePacketReviewTrails } from 'hooks/api/usePacketReviewTrails'
import { readCommentDeepLink } from 'components/comments/commentDeepLink'
import { ReviewCreativeCard } from './review/ReviewCreativeCard'
import { ReviewCreativePreviewModal } from './review/ReviewCreativePreviewModal'
import { ReviewHistory } from './review/ReviewHistory'
import { ReviewHistoryFeed } from './review/ReviewHistoryFeed'
import { ReviewThemeSlide } from './review/ReviewThemeSlide'
import { activeReviewedWith, ReviewingWith, ReviewingWithState } from './review/ReviewingWith'
import { uploadReviewMedia } from './review/uploadReviewMedia'
import { reviewMediaLibrary } from './review/reviewMediaLibrary'
import { adPosition, adStepKey, buildReviewSteps } from './review/reviewSteps'
import { useReviewKeyNav } from './review/useReviewKeyNav'
import { CreativeCardList, CreativeGroupEditing } from './editor/sections/CreativeCardList'
import { NarrativeEditor } from './editor/sections/NarrativeEditor'
import { joinStrategy } from './editor/sections/joinStrategy'
import { usePacketFonts } from './usePacketFonts'
import { dark, radius, type as typo } from './tokens'

// One option list owns the ids, the labels and the predicates, so the visible
// list and the button counts read from the same predicate and cannot drift.
//
// Bucketed on `creative.reviewStatus` — the same field `StatusPill` renders
// from — rather than on the external review rows `ReviewCreativeCard` derives
// its note line from. Deriving would drop an ad whose pill reads "Rejected By
// Staff" out of Rejected, and a filter that disagrees with the badge on the
// card is a bug report waiting to happen.
//
// `Published` lands in no bucket but All: a published creative can't be
// reviewed at all (the server refuses the verdict and the card hides its
// buttons), so it must not read as outstanding work in a bucket someone is
// emptying — and calling it Approved to a client is a false claim, since a
// sprint can hold ads published without any client verdict. The consequence is
// deliberate: in a packet with published ads the three counts don't sum to All,
// because the buckets partition the *reviewable* ads.
//
// Edited after review (DRI-190) is the one filter that isn't a verdict, and it
// sits outside that rule on purpose: it holds every ad edited after any client
// verdict, whatever its badge says now, so it overlaps the verdict buckets. That
// overlap is why it's set apart after a divider (`separate`). It reads the
// packet's review trails rather than the creative.
type FilterMatch = (creative: AdCreative, trail?: CreativeReviewTrail) => boolean

const REVIEW_FILTERS: ReadonlyArray<{
  id: 'all' | 'rejected' | 'approved' | 'unreviewed' | 'edited'
  label: string
  matches: FilterMatch
  separate?: boolean
}> = [
  { id: 'all', label: 'All', matches: () => true },
  {
    id: 'rejected',
    label: 'Rejected',
    matches: (creative: AdCreative) => creative.reviewStatus === AssetApprovalStatus.Rejected,
  },
  {
    id: 'approved',
    label: 'Approved',
    matches: (creative: AdCreative) => creative.reviewStatus === AssetApprovalStatus.Approved,
  },
  {
    id: 'unreviewed',
    label: 'Not yet reviewed',
    // `Ready` is internally approved with no client verdict yet, so it is
    // genuinely not reviewed *here*.
    matches: (creative: AdCreative) =>
      creative.reviewStatus === AssetApprovalStatus.Draft ||
      creative.reviewStatus === AssetApprovalStatus.Pending ||
      creative.reviewStatus === AssetApprovalStatus.Ready,
  },
  {
    id: 'edited',
    label: 'Edited after review',
    matches: (_creative, trail) => !!trail?.editedAfterReview,
    separate: true,
  },
]

type ReviewFilter = typeof REVIEW_FILTERS[number]['id']

// The one place ads get reviewed, for clients and employees alike: instead of
// paging through the deck, this lists every ad in the sprint so it can be
// approved or rejected directly (with a reason on rejection).
//
// An employee's verdict IS the client's — an approval reads Approved everywhere,
// and a rejection gates the ad out of the campaign picker exactly as the client's
// own would — so both cohorts hit the same endpoint with the same buttons. What
// employees additionally get is the ability to edit the ad's copy in place, undo
// a recorded approval, and author the sprint's creative strategy — renaming a
// theme, moving an ad between themes, unpinning, regenerating the grouping.
// Clients only ever approve or reject, and read the themes as plain text.
export function ReviewSprintPacket() {
  usePacketFonts()
  const { businessID, packetID } = useParams<{ businessID: string; packetID: string }>()
  const businessIDNum = Number(businessID)

  const { search } = useLocation()

  // Builder access controls optional UI and broad cache invalidation; actor identity
  // controls the verdict flag the server requires. Keep them separate so an employee
  // with review-only access neither 400s nor receives builder-only affordances.
  const { user } = useContext(sessionContext)
  const hasBuilderAccess = !!user?.permissions.includes(Permissions.AccessAdBuilderApp)
  const asClientVerdict = !user || user.type !== 'Client'

  // Who the employee is reviewing with (DRI-161), asked once per visit and
  // carried into every verdict's audit comment. Clients are never asked — the
  // form only renders for a signed-in non-client below.
  const [reviewingWith, setReviewingWith] = useState<ReviewingWithState>({
    kind: 'asking',
    draft: '',
  })
  const showReviewingWith = !!user && user.type !== 'Client'

  const { packet, isLoading: packetLoading } = useSprintPacket(Number(packetID))
  const { sprintCreatives, sprintCreativesLoading } = useSprint(packet?.sprintID)
  const { strategy, isLoading: strategyLoading } = usePacketStrategy(Number(packetID))
  const { trails } = usePacketReviewTrails(businessIDNum, Number(packetID))
  const matchesFilter = (option: typeof REVIEW_FILTERS[number], creative: AdCreative) =>
    option.matches(creative, trails.get(creative.id))

  // One mutation for the whole grid rather than one per card. ~50 cards and the
  // modal can all submit a verdict, and giving each its own is how the quick
  // Approve and the modal's Reject drift apart.
  const { submitVerdict, removeApproval } = useAdCreativeReview(businessIDNum, {
    asClientVerdict,
    hasBuilderAccess,
    reviewedWith: showReviewingWith ? activeReviewedWith(reviewingWith) : null,
  })
  const submitting = submitVerdict.isLoading || removeApproval.isLoading

  const updateCopy = useUpdateCreativeCopy(businessIDNum)

  // Theme authoring. Every mutation returns the full refreshed strategy, so the
  // hooks just replace the query's data — no local reconciliation.
  const regenerate = useRegenerateStrategy(Number(packetID))
  const updateGroup = useUpdateStrategyGroup(Number(packetID))
  const createGroup = useCreateStrategyGroup(Number(packetID))
  const deleteGroup = useDeleteStrategyGroup(Number(packetID))
  const moveAd = useMoveStrategyAd(Number(packetID))
  const unpinAd = useUnpinStrategyAd(Number(packetID))

  // An employee authors the strategy on the page they review from, rather than
  // going back to the packet editor to move an ad they just spotted under the
  // wrong theme. Left on rather than behind a mode switch, matching the preview
  // modal, which already edits copy and media in front of the client.
  const editing: CreativeGroupEditing | undefined = hasBuilderAccess
    ? {
        groups: strategy?.groups ?? [],
        onSaveGroup: (groupID, changes) => updateGroup.mutate({ groupID, dto: changes }),
        onCreateGroup: (creativeID, label) => createGroup.mutate({ creativeID, label }),
        onDeleteGroup: (groupID, moveToGroupID) =>
          deleteGroup.mutate({ groupID, dto: { moveToGroupID } }),
        onMove: (creativeID, groupID) => moveAd.mutate({ creativeID, dto: { groupID } }),
        onUnpin: creativeID => unpinAd.mutate({ creativeID }),
        onRegenerate: () => regenerate.mutate(),
        regenerating: regenerate.isLoading || strategy?.status === 'generating',
      }
    : undefined

  const loading = packetLoading || sprintCreativesLoading || strategyLoading
  const creatives = sprintCreatives ?? []

  // ~50 ads in a packet and no way to find the handful a client rejected, or the
  // ones they haven't reached yet. One filtered list feeds both the grid and the
  // walkthrough, which is what keeps the modal's prev/next in agreement with
  // what's on screen — with Rejected active, next is a work queue rather than a
  // tour of the whole packet.
  // `?filter=` picks the starting bucket, so a link can hand someone the work rather
  // than the whole packet — the draft-campaign launcher's route out of a rejection
  // arrives on Rejected. Only the initial value: changing the tabs afterwards is not
  // worth a history entry, and an unknown value falls back to All.
  const [filter, setFilter] = useState<ReviewFilter>(() => {
    const requested = new URLSearchParams(search).get('filter')
    return REVIEW_FILTERS.some(option => option.id === requested)
      ? (requested as ReviewFilter)
      : 'all'
  })
  const selected = REVIEW_FILTERS.find(option => option.id === filter) ?? REVIEW_FILTERS[0]

  // One atomic object rather than two separate `useState`s — `handleAdvance`
  // below fires after an `await` (a promise continuation, not a React event
  // handler), where React doesn't guarantee batching two separate `setState`
  // calls into a single render. With two states, that produced a real bug: a
  // render where the id had already moved to the next ad but the mode hadn't
  // caught up yet, mounting that ad's modal in reject mode by mistake — and
  // once mounted, its local `rejecting` state stuck there even once the mode
  // prop corrected itself a render later. One `setOpen` call can't split like
  // that.
  const [open, setOpen] = useState<{ stepKey: string; mode: 'view' | 'reject' } | null>(null)

  // The ad being read stays in the list whether or not it still matches: saving an
  // edit clears the client's approval, and under the Approved filter that would
  // otherwise pull the ad out from under the reviewer mid-edit. It leaves when
  // they do.
  const visible = creatives.filter(
    creative => matchesFilter(selected, creative) || adStepKey(creative.id) === open?.stepKey
  )

  // The order the walkthrough runs in, matching what's on screen — each theme's
  // slide, then its ads, then anything ungrouped. The raw sprint list is a
  // different order entirely once grouping applies, so prev/next/advance walk
  // this and never the sprint's own order.
  const steps = buildReviewSteps(joinStrategy(strategy, visible))

  const introText =
    packet?.narrative?.find(block => block.sectionKey === 'creative-strategy')?.richText?.trim() ??
    ''

  const openIndex = open ? steps.findIndex(step => step.key === open.stepKey) : -1
  const openStep = openIndex >= 0 ? steps[openIndex] : null

  const openAt = (creativeID: number, mode: 'view' | 'reject' = 'view') =>
    setOpen({ stepKey: adStepKey(creativeID), mode })

  // `?ad=` opens one ad on arrival — the packet editor's cards and the packet's
  // comment deep links both land here. Deliberately not on mount: the packet, its
  // ads and the strategy all load asynchronously, so `steps` is empty then. Fires
  // once, so closing the ad doesn't reopen it; an id that isn't in this packet
  // opens nothing.
  const requestedAd = new URLSearchParams(search).get('ad')

  // The history column's open state lives here, not in the modal: the modal is
  // remounted per ad, so an employee who opened history would lose it on every
  // prev/next.
  const [historyOpen, setHistoryOpen] = useState(false)
  // The comment a deep link asked to read, handed to whichever feed is mounted.
  // The feed ignores an id that isn't in the ad it is showing, so walking off the
  // linked ad simply stops matching.
  const [targetCommentID, setTargetCommentID] = useState<number | null>(null)

  const deepLinkHandled = useRef(false)
  useEffect(() => {
    if (deepLinkHandled.current || loading || !requestedAd) return
    const stepKey = adStepKey(Number(requestedAd))
    if (!steps.some(step => step.key === stepKey)) return
    deepLinkHandled.current = true
    setOpen({ stepKey, mode: 'view' })

    // A creative comment link arrives with `?ad=` and the shared comment params
    // together. Anything that doesn't name a comment on this same ad — another
    // anchor type, a different creative, a malformed id — opens the ad and
    // nothing more.
    const link = readCommentDeepLink(search)
    const commentID = Number(new URLSearchParams(search).get('commentId'))
    if (link?.anchorType !== 'creative' || link.anchorId !== requestedAd) return
    if (!Number.isInteger(commentID) || commentID <= 0) return
    setTargetCommentID(commentID)
    setHistoryOpen(true)
  }, [loading, requestedAd, search, steps])

  // Successful approve/reject advances to the next step (slideshow-style) —
  // whatever comes next in the displayed order, regardless of which ad the client
  // started on — and only actually closes once there's nothing after it. Crossing
  // into a new theme lands on that theme's slide rather than straight onto its
  // first ad, which is the only marker that the reasoning changed.
  //
  // `steps` and `openIndex` are this render's, so a verdict whose refetch drops
  // the ad out of the active filter still advances to the successor it had when
  // the modal was open, rather than to whatever the shortened list now starts
  // with.
  const handleAdvance = () => {
    const next = steps[openIndex + 1]
    setOpen(next ? { stepKey: next.key, mode: 'view' } : null)
  }

  // Manual back/forward through the deck, independent of approving/rejecting —
  // always lands in view mode.
  const goToPrev = () => {
    const prev = steps[openIndex - 1]
    if (prev) setOpen({ stepKey: prev.key, mode: 'view' })
  }
  const goToNext = () => {
    const next = steps[openIndex + 1]
    if (next) setOpen({ stepKey: next.key, mode: 'view' })
  }

  useReviewKeyNav({ active: !!openStep, onPrev: goToPrev, onNext: goToNext })

  return (
    <div style={{ minHeight: '100vh', background: dark.bg }}>
      {/* The eyebrow and the way back out are pinned. This page is dark and
        reads as a modal, so the instinct on arriving is to dismiss it rather
        than to scroll for an exit; the heading can scroll away, the label
        saying where you are and the link out cannot. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: dark.bg,
          borderBottom: `1px solid ${dark.hairline}`,
        }}
      >
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 24,
          }}
        >
          <div
            style={{
              fontFamily: typo.family.sans,
              fontSize: typo.size.eyebrow,
              fontWeight: typo.weight.semibold,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: dark.accent,
            }}
          >
            Client review
          </div>

          <Link
            to={`/${businessID}/sprint-packets/${packetID}/present#creative`}
            style={{
              flexShrink: 0,
              fontFamily: typo.family.sans,
              fontSize: typo.size.body,
              fontWeight: typo.weight.semibold,
              color: dark.accent,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            View full sprint packet →
          </Link>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 32px 48px' }}>
        <h1
          style={{
            margin: 0,
            fontFamily: typo.family.serif,
            fontWeight: typo.weight.regular,
            fontSize: typo.size.h1,
            color: dark.text,
          }}
        >
          Review ads
        </h1>
        <p
          style={{
            marginTop: 8,
            marginBottom: 0,
            fontFamily: typo.family.sans,
            fontSize: typo.size.bodyLg,
            color: dark.textMuted,
          }}
        >
          {packet ? packet.sprint?.name ?? `Sprint #${packet.sprintID}` : ''}
        </p>

        {/* The creative section's opening prose, same block the deck reads —
          this page is the client's other way into the same section, so it
          shouldn't arrive stripped of the reasoning.

          An employee writes it here, in the type the client will read it in, so
          the length and line breaks they judge are the ones that ship. It stays
          mounted when empty, since the placeholder is the prompt to write one;
          a client with nothing to read gets nothing rather than that prompt. */}
        {hasBuilderAccess ? (
          <div style={{ marginTop: 32 }}>
            <NarrativeEditor
              quiet
              palette={dark}
              sectionKey="creative-strategy"
              label="Creative strategy"
              packetID={Number(packetID)}
              narrative={packet?.narrative ?? []}
              placeholder="The thinking behind this sprint's creative…"
              inputStyle={{
                background: 'transparent',
                color: dark.text,
                fontFamily: typo.family.serif,
                fontSize: 24,
                lineHeight: 1.45,
              }}
            />
          </div>
        ) : (
          introText && (
            <p
              style={{
                marginTop: 32,
                marginBottom: 0,
                fontFamily: typo.family.serif,
                fontSize: 24,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                color: dark.text,
              }}
            >
              {introText}
            </p>
          )
        )}

        {/* Every bucket renders always, counts included, even at zero — a control
          that disappears when its bucket empties is a control nobody learns. At
          phone widths the row wraps to two per line rather than squeezing the
          long label. Not in the sticky header: that band is deliberately just
          the eyebrow and the way out. */}
        {!loading && creatives.length > 0 && showReviewingWith && (
          <ReviewingWith state={reviewingWith} onChange={setReviewingWith} />
        )}

        {!loading && creatives.length > 0 && (
          <div
            role="group"
            aria-label="Filter ads by review status"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginTop: showReviewingWith && reviewingWith.kind !== 'none' ? 24 : 32,
              marginBottom: 36,
            }}
          >
            {REVIEW_FILTERS.map(option => {
              const active = option.id === filter
              return (
                <React.Fragment key={option.id}>
                  {option.separate && (
                    <span
                      aria-hidden="true"
                      style={{
                        width: 1,
                        alignSelf: 'stretch',
                        background: dark.hairline,
                        margin: '0 6px',
                      }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setFilter(option.id)}
                    aria-pressed={active}
                    style={{
                      // The longest label; at 140 it wraps a line early on phones.
                      flex: option.separate ? '1 1 180px' : '1 1 140px',
                      padding: '8px 12px',
                      borderRadius: radius.sm,
                      border: `1px solid ${active ? dark.accent : dark.hairline}`,
                      background: active ? dark.accent : 'transparent',
                      color: active ? dark.accentInk : dark.textMuted,
                      fontFamily: typo.family.sans,
                      fontSize: typo.size.body,
                      fontWeight: typo.weight.medium,
                      cursor: 'pointer',
                    }}
                  >
                    {option.label} ·{' '}
                    {creatives.filter(creative => matchesFilter(option, creative)).length}
                  </button>
                </React.Fragment>
              )
            })}
          </div>
        )}

        {(loading || creatives.length === 0) && <div style={{ marginBottom: 36 }} />}

        {loading && (
          <div style={{ fontFamily: typo.family.sans, color: dark.textMuted }}>Loading…</div>
        )}

        {!loading && creatives.length === 0 && (
          <div style={{ fontFamily: typo.family.sans, color: dark.textMuted }}>
            There are no ads to review yet.
          </div>
        )}

        {/* The page owns this rather than letting the list fall through to its own
          "No creatives in this sprint yet." — that copy addresses an AE looking at
          an empty sprint and would be plainly wrong about a packet full of ads.
          The filter deliberately doesn't reset itself when its bucket empties:
          the page moving on its own is worse than an empty state that says what
          happened. */}
        {!loading && creatives.length > 0 && visible.length === 0 && (
          <div style={{ fontFamily: typo.family.sans, color: dark.textMuted }}>
            <p style={{ margin: 0, fontSize: typo.size.bodyLg }}>
              Nothing to show under “{selected.label}”.
            </p>
            <button
              type="button"
              onClick={() => setFilter('all')}
              style={{
                marginTop: 12,
                padding: '8px 12px',
                borderRadius: radius.sm,
                border: `1px solid ${dark.hairline}`,
                background: 'transparent',
                color: dark.accent,
                fontFamily: typo.family.sans,
                fontSize: typo.size.body,
                fontWeight: typo.weight.semibold,
                cursor: 'pointer',
              }}
            >
              Show all ads
            </button>
          </div>
        )}

        {/* Ads grouped by theme. Loading and empty are handled above rather
          than by the list, whose own copy addresses an AE. */}
        {!loading && visible.length > 0 && (
          <CreativeCardList
            palette={dark}
            creatives={visible}
            strategy={strategy}
            editing={editing}
            renderCard={(creative, { pinned }) => (
              <ReviewCreativeCard
                creative={creative}
                onOpen={() => openAt(creative.id)}
                onOpenReject={() => openAt(creative.id, 'reject')}
                onApprove={() => submitVerdict.mutate({ creativeID: creative.id, approved: true })}
                submitting={submitting}
                pinned={pinned}
                editing={editing}
                trail={trails.get(creative.id)}
              />
            )}
          />
        )}
      </div>

      {openStep?.kind === 'theme' && (
        <ReviewThemeSlide
          key={openStep.key}
          label={openStep.group.label}
          blurb={openStep.group.blurb}
          adCount={openStep.adCount}
          hasPrev={openIndex > 0}
          hasNext={openIndex < steps.length - 1}
          onPrev={goToPrev}
          onNext={goToNext}
          onClose={() => setOpen(null)}
        />
      )}

      {openStep?.kind === 'ad' && (
        <ReviewCreativePreviewModal
          key={openStep.key}
          creative={openStep.creative}
          startInReject={open?.mode === 'reject'}
          position={adPosition(steps, openIndex)}
          themeLabel={openStep.themeLabel}
          hasPrev={openIndex > 0}
          hasNext={openIndex < steps.length - 1}
          onPrev={goToPrev}
          onNext={goToNext}
          onSubmitVerdict={verdict =>
            submitVerdict.mutateAsync({ creativeID: openStep.creative.id, ...verdict })
          }
          submitting={submitting}
          onRemoveApproval={
            hasBuilderAccess ? () => removeApproval.mutate(openStep.creative.id) : undefined
          }
          onSaveCopy={
            hasBuilderAccess
              ? patch => updateCopy.mutateAsync({ creativeID: openStep.creative.id, patch })
              : undefined
          }
          onUploadMedia={
            hasBuilderAccess ? file => uploadReviewMedia(businessIDNum, file) : undefined
          }
          mediaLibrary={hasBuilderAccess ? reviewMediaLibrary(businessIDNum) : undefined}
          historySlot={
            hasBuilderAccess ? (
              <ReviewHistory open={historyOpen} onToggle={() => setHistoryOpen(value => !value)}>
                {historyOpen && (
                  <ReviewHistoryFeed
                    businessID={businessIDNum}
                    creativeID={openStep.creative.id}
                    targetCommentID={targetCommentID}
                  />
                )}
              </ReviewHistory>
            ) : undefined
          }
          onAdvance={handleAdvance}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}
